<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const openApp = () => {
  window.open('https://app.neurals.pro', '_blank')
}

// Control del flujo animado
const activeStep = ref(0)
let flowTimer = null

const steps = ref([
  {
    number: '01',
    title: 'Conecta tu WhatsApp',
    subtitle: 'Activación en segundos.',
    description: 'Escanea el código QR desde la plataforma para vincular tu número comercial. Sin esperas ni aprobaciones largas.',
    icon: '💬',
    badge: 'Gestión Multilínea'
  },
  {
    number: '02',
    title: 'Sube tu Catálogo',
    subtitle: 'Alimenta el cerebro cognitivo.',
    description: 'Carga tus productos. El agente indexa todo al instante para cotizar con precisión absoluta y otorgándole un dominio total sobre tus productos y servicios.',
    icon: '🗄️',
    badge: 'Conocimiento Activo'
  },
  {
    number: '03',
    title: 'Piloto Automático',
    subtitle: 'Captura y cierra 24/7.',
    description: '¡Listo! Tu agente comienza a conversar de forma natural, identificando requerimientos y enviando leads cualificados directo a tu equipo.',
    icon: '⚡',
    badge: 'Generación de Leads'
  }
])

// Lógica de Scroll Reveal
const sectionRef = ref(null)
const isVisible = ref(false)
let observer = null

const startFlowAnimation = () => {
  // Cambia el paso activo cada 3 segundos
  flowTimer = setInterval(() => {
    activeStep.value = (activeStep.value + 1) % steps.value.length
  }, 3000)
}

onMounted(() => {
  observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      isVisible.value = true
      observer.disconnect()
      startFlowAnimation() // Iniciar la animación solo cuando se ve la sección
    }
  }, { threshold: 0.2 })

  if (sectionRef.value) {
    observer.observe(sectionRef.value)
  }
})

onUnmounted(() => {
  if (observer) observer.disconnect()
  if (flowTimer) clearInterval(flowTimer)
})
</script>

<template>
  <section ref="sectionRef" id="capacidades" class="py-24 md:py-32 bg-neural-bg text-neural-text relative overflow-hidden border-b border-neural-border">

    <!-- EFECTOS DE FONDO: Clean Deep Tech Glow (Sin Cuadrícula) -->
    <div class="absolute inset-0 bg-neural-bg pointer-events-none"></div>
    <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[45rem] h-[30rem] bg-neural-primary/10 rounded-full blur-[160px] pointer-events-none animate-pulse"></div>

    <div class="container mx-auto px-6 lg:px-12 relative z-10">

      <!-- CABECERA DE LA SECCIÓN -->
      <div
        class="max-w-3xl mx-auto text-center space-y-5 mb-20 transition-all duration-1000 transform"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'"
      >

        <h2 class="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-neural-text leading-[1.15]">
          No necesitas ser programador. <br class="hidden sm:inline">
          Despliega tu agente en
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-neural-primary to-neural-accent">
            minutos.
          </span>
        </h2>

        <p class="text-base sm:text-lg text-neural-muted font-medium leading-relaxed max-w-2xl mx-auto">
          Olvídate de contratar equipos de ingeniería o lidiar con integraciones complejas. Sigue estos 3 pasos e inicia tu piloto automático hoy mismo.
        </p>
      </div>

      <!-- GRID DE LOS 3 PASOS CON LÍNEA CONECTORA ANIMADA -->
      <div class="relative max-w-6xl mx-auto">

        <!-- LÍNEA CONECTORA HORIZONTAL (Desktop) -->
        <div class="hidden md:block absolute top-1/2 left-[10%] w-[80%] -translate-y-14 z-0">
          <div class="absolute w-full h-[2px] bg-neural-border/50 rounded-full overflow-hidden">
            <div class="w-1/2 h-full bg-gradient-to-r from-transparent via-neural-primary to-transparent animate-[laser-flow_2.5s_ease-in-out_infinite]"></div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10 relative z-10">

          <!-- TARJETAS DE PASOS -->
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="group relative rounded-[2rem] p-8 transition-all duration-700 transform flex flex-col justify-between backdrop-blur-xl hover:-translate-y-2 cursor-default"
            :class="[
              activeStep === index
                ? 'bg-[#131316]/90 text-white shadow-[0_0_40px_rgba(99,102,241,0.2)] border-2 border-neural-primary/60 scale-[1.02]'
                : 'bg-neural-card/60 text-neural-text shadow-xl border border-neural-border hover:border-neural-muted/50 hover:bg-neural-card/90 scale-100',
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            ]"
            :style="{ transitionDelay: isVisible ? `${index * 150}ms` : '0ms' }"
            @mouseenter="activeStep = index"
          >
            <!-- Resplandor interno en estado activo o hover -->
            <div
              class="absolute inset-0 bg-gradient-to-b from-neural-primary/10 to-transparent rounded-[2rem] transition-opacity duration-500 pointer-events-none"
              :class="activeStep === index ? 'opacity-100' : 'opacity-0'"
            ></div>

            <div>
              <div class="flex items-center justify-between mb-8 relative z-10">
                <!-- Ícono animado flotante -->
                <div
                  class="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl transition-all duration-500 shadow-inner"
                  :class="activeStep === index ? 'bg-neural-primary text-white shadow-glow-primary scale-110 -rotate-3' : 'bg-[#27272A] border border-neural-border grayscale opacity-70'"
                >
                  <!-- Animaciones CSS específicas por ícono -->
                  <span :class="{
                    'animate-pulse': index === 0 && activeStep === index,
                    'animate-bounce': index === 1 && activeStep === index,
                    'animate-[spin_1s_ease-in-out]': index === 2 && activeStep === index
                  }">
                    {{ step.icon }}
                  </span>
                </div>

                <!-- Número de Paso (01, 02, 03) -->
                <span
                  class="text-4xl lg:text-5xl font-black font-mono tracking-tighter transition-all duration-500"
                  :class="activeStep === index ? 'text-neural-primary/60' : 'text-neural-muted/20'"
                >
                  {{ step.number }}
                </span>
              </div>

              <!-- Badge de característica -->
              <div class="mb-4 relative z-10">
                <span
                  class="text-[10px] font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full border backdrop-blur-sm transition-colors duration-500"
                  :class="activeStep === index ? 'bg-neural-primary/20 text-neural-text border-neural-primary/50' : 'bg-neural-bg/50 text-neural-muted border-neural-border'"
                >
                  {{ step.badge }}
                </span>
              </div>

              <!-- Título y Subtítulo -->
              <h3 class="text-xl sm:text-2xl font-bold tracking-tight mb-2 relative z-10 transition-colors">
                {{ step.title }}
              </h3>

              <p
                class="text-xs sm:text-sm font-semibold mb-4 relative z-10 transition-colors duration-500"
                :class="activeStep === index ? 'text-neural-accent' : 'text-neural-primary'"
              >
                {{ step.subtitle }}
              </p>

              <!-- Descripción -->
              <p
                class="text-sm leading-relaxed relative z-10 transition-colors duration-500"
                :class="activeStep === index ? 'text-neural-text/90' : 'text-neural-muted'"
              >
                {{ step.description }}
              </p>
            </div>

            <!-- Mini indicador inferior de conexión -->
            <div class="mt-8 pt-5 border-t flex items-center justify-between text-[10px] font-bold uppercase tracking-widest relative z-10 transition-colors duration-500"
              :class="activeStep === index ? 'border-neural-primary/30 text-neural-primary' : 'border-neural-border text-neural-muted/50'"
            >
              <span>{{ activeStep === index ? 'Procesando' : 'En Espera' }}</span>
              <span class="flex items-center gap-1.5 transition-transform" :class="activeStep === index ? 'translate-x-1' : ''">
                {{ index === 2 ? 'Agente Activo' : 'Siguiente' }}
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </span>
            </div>
          </div>

        </div>
      </div>

      <!-- BLOQUE INFERIOR: LLAMADO A LA ACCIÓN PLG (Directo a la App) -->
      <div
        class="mt-16 md:mt-24 text-center transition-all duration-1000 transform"
        :class="isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'"
        style="transition-delay: 600ms;"
      >
        <div class="relative inline-flex flex-col sm:flex-row items-center justify-center gap-6 bg-neural-card/40 backdrop-blur-xl p-2 sm:p-2 sm:pl-8 rounded-3xl sm:rounded-full border border-neural-border shadow-[0_10px_30px_rgba(0,0,0,0.5)] max-w-3xl mx-auto overflow-hidden group hover:border-neural-primary/40 transition-colors">

          <!-- Brillo de fondo del panel -->
          <div class="absolute inset-0 bg-gradient-to-r from-neural-primary/5 to-neural-accent/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>

          <div class="text-left pt-4 sm:pt-0 px-4 sm:px-0 relative z-10">
            <h4 class="text-sm sm:text-base font-bold text-neural-text">¿Listo para probar el poder cognitivo?</h4>
            <p class="text-xs text-neural-muted mt-0.5">Accede a la plataforma y despliega tu agente hoy mismo.</p>
          </div>

          <button
            @click="openApp"
            class="relative z-10 w-full sm:w-auto px-8 py-3.5 rounded-2xl sm:rounded-full bg-neural-primary hover:bg-neural-primary-hover text-white font-bold text-sm tracking-wide transition-all duration-300 shadow-glow-primary hover:shadow-[0_0_20px_rgba(99,102,241,0.6)] flex items-center justify-center gap-2 m-1"
          >
            <span>Iniciar 3 Días Gratis</span>
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
          </button>
        </div>
      </div>

    </div>
  </section>
</template>

<style scoped>
/* Animación de la línea de datos simulando transferencia rápida (SaaS) */
@keyframes laser-flow {
  0% { transform: translateX(-100%); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateX(200%); opacity: 0; }
}
</style>